import { withStyles } from '@mui/styles';
import React from 'react';
import { connect } from 'react-redux';
import { getAvailableDevices } from '../../base/devices/actions.web';
import AbstractDialogTab from '../../base/dialog/components/web/AbstractDialogTab';
import { translate } from '../../base/i18n/functions';
import { createLocalTrack } from '../../base/lib-jitsi-meet/functions.web';
import Checkbox from '../../base/ui/components/web/Checkbox';
import Slider from '../../base/ui/components/web/Slider';
import { inklusivaSettingsStyles } from '../../inklusiva/ui-constants';
import { iAmVisitor as iAmVisitorCheck } from '../../visitors/functions';
import logger from '../logger';
import AudioInputPreview from './AudioInputPreview';
import AudioOutputPreview from './AudioOutputPreview';
import DeviceHidContainer from './DeviceHidContainer.web';
import DeviceSelector from './DeviceSelector.web';
const styles = (theme) => {
    return {
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        inputContainer: {
            marginBottom: theme.spacing(3)
        },
        outputContainer: {
            margin: `${theme.spacing(5)} 0`,
            display: 'flex',
            alignItems: 'flex-end'
        },
        outputButton: {
            marginLeft: theme.spacing(3)
        },
        noiseSuppressionContainer: {
            marginTop: theme.spacing(3),
            marginBottom: theme.spacing(3)
        },
        ownAudioActiveContainer: {
            marginBottom: theme.spacing(5)
        },
        othersAudioActiveContainer: {
            marginBottom: theme.spacing(3)
        },
        ...inklusivaSettingsStyles(theme)
    };
};
/**
 * React {@code Component} for previewing audio and video input/output devices.
 *
 * @augments Component
 */
class AudioDevicesSelection extends AbstractDialogTab {
    /**
     * Initializes a new DeviceSelection instance.
     *
     * @param {Object} props - The read-only React Component props with which
     * the new instance is to be initialized.
     */
    constructor(props) {
        super(props);
        this.state = {
            previewAudioTrack: null
        };
        this._unMounted = true;
    }
    /**
     * Generate the initial previews for audio input and video input.
     *
     * @inheritdoc
     */
    componentDidMount() {
        this._unMounted = false;
        Promise.all([this._createAudioInputTrack(this.props.selectedAudioInputId)])
            .catch(err => logger.warn('Failed to initialize preview tracks', err))
            .then(() => {
            this.props.dispatch(getAvailableDevices());
        });
    }
    /**
     * Checks if audio / video permissions were granted. Updates audio input and
     * video input previews.
     *
     * @param {Object} prevProps - Previous props this component received.
     * @returns {void}
     */
    componentDidUpdate(prevProps) {
        if (prevProps.selectedAudioInputId !== this.props.selectedAudioInputId) {
            this._createAudioInputTrack(this.props.selectedAudioInputId);
        }
    }
    /**
     * Ensure preview tracks are destroyed to prevent continued use.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        this._unMounted = true;
        this._disposeAudioInputPreview();
    }
    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     */
    render() {
        const { classes, hasAudioPermission, hideAudioInputPreview, hideAudioOutputPreview, hideDeviceHIDContainer, hideNoiseSuppression, iAmVisitor, noiseSuppressionEnabled, selectedAudioOutputId, othersAudio, othersVolume, highFrequencies, amplify, balance, acousticCues, t } = this.props;
        const { audioInput, audioOutput } = this._getSelectors();
        const getAmplifyTextValue = (value) => t(`toolbar.amplifyTexts.value${value}`);
        const getBalanceTextValue = (value) => t(`toolbar.balanceTexts.value${value}`);
        return (React.createElement("div", { className: classes.container },
            React.createElement("h2", null, t('settings.audioSettings.headline')),
            React.createElement("p", { className: 'mt-05' },
                " ",
                t('settings.audioSettings.intro'),
                " "),
            !iAmVisitor && (React.createElement("div", { "aria-live": 'polite', className: classes.inputContainer }, this._renderSelector(audioInput))),
            !hideAudioInputPreview && hasAudioPermission && !iAmVisitor
                && React.createElement(AudioInputPreview, { track: this.state.previewAudioTrack }),
            React.createElement("div", { "aria-live": 'polite', className: classes.outputContainer },
                this._renderSelector(audioOutput),
                !hideAudioOutputPreview && hasAudioPermission
                    && React.createElement(AudioOutputPreview, { className: classes.outputButton, deviceId: selectedAudioOutputId })),
            !hideDeviceHIDContainer && !iAmVisitor && React.createElement(DeviceHidContainer, null),
            React.createElement("div", { className: classes.inputblockContainer },
                React.createElement("h3", null, "H\u00F6rbare Signale"),
                React.createElement(Checkbox, { checked: acousticCues, className: classes.inputElement, label: t('settings.audioSettings.acousticCues.label'), name: 'acousticcues_enable', onChange: () => super._onChange({
                        acousticCues: !acousticCues
                    }) }),
                React.createElement("p", { className: 'mt-05' }, t('settings.audioSettings.acousticCues.description'))),
            React.createElement("div", { className: classes.inputblockContainer },
                React.createElement("h3", null, "Teilnehmenden-Ton"),
                React.createElement(Checkbox, { checked: othersAudio, label: t('settings.audioSettings.activateOthersAudio'), onChange: () => super._onChange({
                        othersAudio: !othersAudio
                    }) }),
                React.createElement("p", { className: 'mt-05' }, t('settings.audioSettings.participantAudio.label'))),
            React.createElement("div", { className: classes.inputblockContainer },
                React.createElement("div", { className: classes.controlContainer },
                    React.createElement("div", { className: classes.controlColumn },
                        React.createElement(Slider, { id: 'volume-slider', defaultValue: othersVolume * 100, label: t('settings.audioSettings.othersVolume'), max: 100, min: 0, onChange: event => {
                                super._onChange({
                                    othersVolume: Number(event.target.value) / 100
                                });
                            }, step: 1 })),
                    React.createElement("div", { className: classes.valueColumn },
                        Math.floor(othersVolume * 100),
                        " %")),
                React.createElement("div", { className: classes.controlContainer },
                    React.createElement("div", { className: classes.controlColumn },
                        React.createElement(Slider, { id: 'frequency-slider', defaultValue: highFrequencies, label: t('settings.audioSettings.highFrequencies'), max: 100, min: 0, onChange: event => super._onChange({
                                highFrequencies: event.target.value
                            }), step: 1 })),
                    React.createElement("div", { className: classes.valueColumn },
                        highFrequencies,
                        " %")),
                React.createElement("div", { className: classes.controlContainer },
                    React.createElement("div", { className: classes.controlColumn },
                        React.createElement(Slider, { id: 'amplify-slider', defaultValue: amplify, label: t('settings.audioSettings.amplify'), max: 3, min: 0, onChange: event => super._onChange({
                                amplify: event.target.value
                            }), step: 1 })),
                    React.createElement("div", { className: classes.valueColumn }, getAmplifyTextValue(amplify))),
                React.createElement("div", { className: classes.controlContainer },
                    React.createElement("div", { className: classes.controlColumn },
                        React.createElement(Slider, { id: 'balance-slider', defaultValue: balance, label: t('settings.audioSettings.balance'), max: 4, min: 0, onChange: event => super._onChange({
                                balance: event.target.value
                            }), step: 1 })),
                    React.createElement("div", { className: classes.valueColumn }, getBalanceTextValue(balance))),
                !hideNoiseSuppression && !iAmVisitor && (React.createElement("div", { className: 'mt-2' },
                    React.createElement(Checkbox, { checked: noiseSuppressionEnabled, label: t('toolbar.enableNoiseSuppression'), onChange: () => super._onChange({
                            noiseSuppressionEnabled: !noiseSuppressionEnabled
                        }) }),
                    React.createElement("p", { className: 'mt-05' }, t('settings.audioSettings.noiseSuppressionDescription')))))));
    }
    /**
     * Creates the JitsiTrack for the audio input preview.
     *
     * @param {string} deviceId - The id of audio input device to preview.
     * @private
     * @returns {void}
     */
    _createAudioInputTrack(deviceId) {
        const { hideAudioInputPreview } = this.props;
        if (hideAudioInputPreview) {
            return;
        }
        return this._disposeAudioInputPreview()
            .then(() => createLocalTrack('audio', deviceId, 5000))
            .then(jitsiLocalTrack => {
            if (this._unMounted) {
                jitsiLocalTrack.dispose();
                return;
            }
            this.setState({
                previewAudioTrack: jitsiLocalTrack
            });
        })
            .catch(() => {
            this.setState({
                previewAudioTrack: null
            });
        });
    }
    /**
     * Utility function for disposing the current audio input preview.
     *
     * @private
     * @returns {Promise}
     */
    _disposeAudioInputPreview() {
        return this.state.previewAudioTrack ? this.state.previewAudioTrack.dispose() : Promise.resolve();
    }
    /**
     * Creates a DeviceSelector instance based on the passed in configuration.
     *
     * @private
     * @param {Object} deviceSelectorProps - The props for the DeviceSelector.
     * @returns {ReactElement}
     */
    _renderSelector(deviceSelectorProps) {
        return deviceSelectorProps ? React.createElement(DeviceSelector, { ...deviceSelectorProps, key: deviceSelectorProps.id }) : null;
    }
    /**
     * Returns object configurations for audio input and output.
     *
     * @private
     * @returns {Object} Configurations.
     */
    _getSelectors() {
        const { availableDevices, hasAudioPermission } = this.props;
        const audioInput = {
            devices: availableDevices.audioInput,
            hasPermission: hasAudioPermission,
            icon: 'icon-microphone',
            isDisabled: this.props.disableAudioInputChange || this.props.disableDeviceChange,
            key: 'audioInput',
            id: 'audioInput',
            label: 'settings.selectMic',
            onSelect: (selectedAudioInputId) => super._onChange({ selectedAudioInputId }),
            selectedDeviceId: this.state.previewAudioTrack
                ? this.state.previewAudioTrack.getDeviceId()
                : this.props.selectedAudioInputId
        };
        let audioOutput;
        if (!this.props.hideAudioOutputSelect) {
            audioOutput = {
                devices: availableDevices.audioOutput,
                hasPermission: hasAudioPermission,
                icon: 'icon-speaker',
                isDisabled: this.props.disableDeviceChange,
                key: 'audioOutput',
                id: 'audioOutput',
                label: 'settings.selectAudioOutput',
                onSelect: (selectedAudioOutputId) => super._onChange({ selectedAudioOutputId }),
                selectedDeviceId: this.props.selectedAudioOutputId
            };
        }
        return { audioInput,
            audioOutput };
    }
}
const mapStateToProps = (state) => {
    return {
        availableDevices: state['features/base/devices'].availableDevices ?? {},
        iAmVisitor: iAmVisitorCheck(state)
    };
};
export default connect(mapStateToProps)(withStyles(styles)(translate(AudioDevicesSelection)));
